import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Loading from 'Components/Atoms/Loading/Loading';
import Button from 'Components/Atoms/Button/Button';
import Amount from 'Components/Molecules/Amount/Amount';
import useCurrency from 'Hooks/useCurrency';
import useLocale from 'Hooks/useLocale';
import { formatCurrency } from 'Utils/formatters';
import { REPLAY_BLOCK_CLASS } from 'Utils/constants';
import { cn } from 'Utils/cn';
import useInfiniteForecastInspectLiquidated from './useInfiniteForecastInspectLiquidated';

const isValidDate = (date) => date instanceof Date && !isNaN(date);

export default function ForecastValueTransactions(props) {
    const { cell, forecastId, ignored = false } = props;

    const { data, isLoading, isFetching, hasNextPage, fetchNextPage } = useInfiniteForecastInspectLiquidated(
        forecastId,
        ignored ? { ...cell.filters, ignored: true } : cell.filters,
    );

    const { t } = useTranslation('common');
    const currency = useCurrency();
    const locale = useLocale();

    const transactions = data?.pages?.reduce((acc, page) => [...acc, ...page.items], []) ?? [];
    const { count } = data?.pages?.[0] ?? { count: 0 };

    const amount = cell.amount_consolidated ?? cell.amount ?? 0;

    return (
        <div className="flex flex-col h-full">
            {isLoading && (
                <div className="py-6 flex justify-center">
                    <Loading />
                </div>
            )}

            <div className="flex h-8 mb-2 items-center justify-start gap-2 px-4">
                {!isLoading && (
                    <div className={cn('text-sm font-medium text-dark-blue/50', REPLAY_BLOCK_CLASS)}>
                        {formatCurrency(amount, { currency, locale })} {t('transactions-list-title', { count })}
                    </div>
                )}
            </div>

            {transactions.length > 0 && (
                <div className="pb-6">
                    <div className="bg-white rounded-md shadow-card">
                        {transactions.map((transaction, index) => (
                            <div key={index} className="px-5 py-3 not-last:border-b border-gray-200">
                                <div className="flex items-center gap-3">
                                    {transaction.provider_connection_product?.provider_logo_url ? (
                                        <img
                                            src={transaction.provider_connection_product.provider_logo_url}
                                            alt={transaction.provider_connection_product.name ?? ''}
                                            className="size-5 shrink-0 rounded object-contain"
                                        />
                                    ) : (
                                        <span className="size-5 shrink-0" aria-hidden="true" />
                                    )}

                                    <div className="grid grid-cols-[1fr_auto] sm:grid-cols-[3fr_1fr_110px] gap-3 grow items-center min-w-0 text-sm">
                                        <div className="truncate">{transaction.description}</div>

                                        <div className="text-gray-500 text-xs truncate hidden sm:block">
                                            {isValidDate(transaction.date_executed)
                                                ? transaction.date_executed.toLocaleDateString(locale)
                                                : null}
                                        </div>

                                        <Amount
                                            amount={transaction.amount_uc}
                                            currency={transaction.currency_uc}
                                            isIncome={transaction.amount_uc > 0}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {!isLoading && transactions.length === 0 && (
                <div className="text-center py-5 my-12">
                    <span className="opacity-50">{t('transactions-no-results')}</span>
                </div>
            )}

            {hasNextPage && (
                <div className="mt-auto pb-4 text-center">
                    {isFetching ? (
                        <Loading size={48} />
                    ) : (
                        <Button onClick={fetchNextPage} size="small">
                            {t('load-more')}
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
}

ForecastValueTransactions.propTypes = {
    cell: PropTypes.object.isRequired,
    forecastId: PropTypes.string.isRequired,
    ignored: PropTypes.bool,
};
